import { useEffect, useMemo, useState } from "react";
import { Accordion, Button, Divider, Form, Header, Icon, Message, Segment } from "semantic-ui-react";
import FloorPriorityTab from "./FloorPriorityTab";

const EMPTY_SETTINGS = {
  roomTurnoverBufferMinutes: 60,
  lateCheckoutGraceMinutes: 30,
  autoReleaseMaintenance: false,
  allowRoomReuse: false,
  autoBookEnabled: false,
  autoBookMaxRooms: 2,
  autoBookMinLeadDays: 3,
  autoBookLookaheadDays: 10,
  autoBookMaxStayNights: "",
};

const settingsFromGh = (gh) => ({
  roomTurnoverBufferMinutes: gh?.roomTurnoverBufferMinutes ?? 60,
  lateCheckoutGraceMinutes: gh?.lateCheckoutGraceMinutes ?? 30,
  autoReleaseMaintenance: gh?.autoReleaseMaintenance ?? false,
  allowRoomReuse: gh?.allowRoomReuse ?? false,
  autoBookEnabled: gh?.autoBookEnabled ?? false,
  autoBookMaxRooms: gh?.autoBookMaxRooms ?? 2,
  autoBookMinLeadDays: gh?.autoBookMinLeadDays ?? 3,
  autoBookLookaheadDays: gh?.autoBookLookaheadDays ?? 10,
  autoBookMaxStayNights: gh?.autoBookMaxStayNights ?? "",
});

/* Normalizes both sides before comparing, so a value round-tripped through a
   number input (which yields strings) doesn't register as "changed" just
   because of a string/number type mismatch against the loaded settings. */
const normalizeSettings = (s) => ({
  roomTurnoverBufferMinutes: Number(s.roomTurnoverBufferMinutes) || 0,
  lateCheckoutGraceMinutes: Number(s.lateCheckoutGraceMinutes) || 0,
  autoReleaseMaintenance: !!s.autoReleaseMaintenance,
  allowRoomReuse: !!s.allowRoomReuse,
  autoBookEnabled: !!s.autoBookEnabled,
  autoBookMaxRooms: Number(s.autoBookMaxRooms) || 0,
  autoBookMinLeadDays: Number(s.autoBookMinLeadDays) || 0,
  autoBookLookaheadDays: Number(s.autoBookLookaheadDays) || 0,
  autoBookMaxStayNights:
    s.autoBookMaxStayNights === "" || s.autoBookMaxStayNights == null
      ? null
      : Number(s.autoBookMaxStayNights),
});

export default function BookingSettingsTab({
  guestHouses,
  rooms,
  floorPriorities,
  onSave,
  onAddFloorPriority,
  onUpdateFloorPriority,
  onDeleteFloorPriority,
}) {
  const [selectedGhId, setSelectedGhId] = useState(guestHouses[0]?.id || "");
  const [form, setForm] = useState(EMPTY_SETTINGS);
  const [advancedOpen, setAdvancedOpen] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  const selectedGh = useMemo(
    () => guestHouses.find((g) => g.id === selectedGhId) || guestHouses[0],
    [guestHouses, selectedGhId],
  );

  useEffect(() => {
    if (!selectedGhId && guestHouses[0]) setSelectedGhId(guestHouses[0].id);
  }, [guestHouses, selectedGhId]);

  /* Keyed on the id: a save (or a background reload) replaces the guest house
     object, and resetting then would wipe the saved message and unsaved edits. */
  useEffect(() => {
    if (!selectedGh) return;
    setForm(settingsFromGh(selectedGh));
    setError("");
    setSaved(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGh?.id]);

  const dirty =
    JSON.stringify(normalizeSettings(form)) !==
    JSON.stringify(normalizeSettings(settingsFromGh(selectedGh)));

  const updateForm = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setSaved(false);
    try {
      await onSave(selectedGh.id, form);
      setSaved(true);
    } catch (e) {
      setError(e.message || "Failed to save booking settings.");
    } finally {
      setSaving(false);
    }
  };

  if (!selectedGh) {
    return <Segment basic>No guest house available.</Segment>;
  }

  return (
    <div>
      <Segment>
        <Form error={!!error} success={saved}>
          {guestHouses.length > 1 && (
            <Form.Select
              label="Guest House"
              options={guestHouses.map((g) => ({ key: g.id, text: g.name, value: g.id }))}
              value={selectedGh.id}
              onChange={(_, data) => setSelectedGhId(data.value)}
            />
          )}

          <Form.Group widths="equal">
            <Form.Input
              label="Room Turnover Buffer (minutes)"
              type="number"
              min={0}
              value={form.roomTurnoverBufferMinutes}
              onChange={(e) => updateForm("roomTurnoverBufferMinutes", e.target.value)}
            />
            <Form.Input
              label="Late Checkout Grace Period (minutes)"
              type="number"
              min={0}
              value={form.lateCheckoutGraceMinutes}
              onChange={(e) => updateForm("lateCheckoutGraceMinutes", e.target.value)}
            />
          </Form.Group>
          <p style={{ fontSize: 12, color: "rgba(0,0,0,0.6)", marginTop: -8 }}>
            Turnover buffer blocks check-in into a room until this many minutes have passed
            since its last checkout, giving housekeeping time to clean it. Late checkout grace
            is how far past a booking's requested checkout time actual checkout can run before
            billing adds an extra night (billing only — no effect on check-in/checkout itself).
          </p>

          <Form.Checkbox
            toggle
            label="Automatically mark rooms available after the turnover buffer"
            checked={form.autoReleaseMaintenance}
            onChange={(_, data) => updateForm("autoReleaseMaintenance", data.checked)}
          />
          <p style={{ fontSize: 12, color: "rgba(0,0,0,0.6)", marginTop: -8 }}>
            Off: after checkout a room stays under maintenance ("Guest checked out — room
            cleaning") until someone marks it available from the Room Map. On: it becomes
            available by itself once the turnover buffer has passed.
          </p>

          <Form.Checkbox
            toggle
            label="Allow a booking to use a room again after it was checked out"
            checked={form.allowRoomReuse}
            onChange={(_, data) => updateForm("allowRoomReuse", data.checked)}
          />
          <p style={{ fontSize: 12, color: "rgba(0,0,0,0.6)", marginTop: -8 }}>
            Off: once a room on a booking is checked out, it can't be added back to that
            booking. On: it can, from the time the turnover buffer after that checkout ends.
          </p>

          <Accordion>
            <Accordion.Title
              active={advancedOpen}
              onClick={() => setAdvancedOpen((v) => !v)}
              index={0}
            >
              <Icon name="dropdown" />
              Auto-Booking
            </Accordion.Title>
            <Accordion.Content active={advancedOpen}>
              <Form.Checkbox
                label="Enable Auto-Booking"
                checked={form.autoBookEnabled}
                onChange={(_, data) => updateForm("autoBookEnabled", data.checked)}
              />
              <Form.Group widths="equal">
                <Form.Input
                  label="Max Rooms per Auto-Booking"
                  type="number"
                  min={1}
                  value={form.autoBookMaxRooms}
                  onChange={(e) => updateForm("autoBookMaxRooms", e.target.value)}
                />
                <Form.Input
                  label="Min Lead Time (days)"
                  type="number"
                  min={0}
                  value={form.autoBookMinLeadDays}
                  onChange={(e) => updateForm("autoBookMinLeadDays", e.target.value)}
                />
                <Form.Input
                  label="Lookahead Window (days)"
                  type="number"
                  min={0}
                  value={form.autoBookLookaheadDays}
                  onChange={(e) => updateForm("autoBookLookaheadDays", e.target.value)}
                />
              </Form.Group>
              <p style={{ fontSize: 12, color: "rgba(0,0,0,0.6)" }}>
                Auto-booking won't fire if the stay is sooner than "Min Lead Time" or as far out
                as (or further than) "Lookahead Window" days from today. It also requires a
                per-room-type threshold to be set (Room Types tab).
              </p>
              <Form.Input
                label="Max Stay Length (nights)"
                type="number"
                min={1}
                placeholder="No limit"
                value={form.autoBookMaxStayNights}
                onChange={(e) => updateForm("autoBookMaxStayNights", e.target.value)}
              />
              <p style={{ fontSize: 12, color: "rgba(0,0,0,0.6)" }}>
                Longer stays fall back to manual review. Leave blank for no limit.
              </p>
            </Accordion.Content>
          </Accordion>

          {error && <Message error content={error} />}
          {saved && <Message success content="Booking settings saved." />}
        </Form>
      </Segment>
      <Segment basic textAlign="right">
        <Button primary loading={saving} disabled={saving || !dirty} onClick={handleSave}>
          <Icon name="save" />
          {saving ? "Saving…" : "Save Changes"}
        </Button>
      </Segment>

      <Divider />
      <Header as="h4">Floor Priority</Header>
      <FloorPriorityTab
        key={selectedGh.id}
        guestHouses={[selectedGh]}
        rooms={rooms}
        floorPriorities={floorPriorities}
        onAdd={onAddFloorPriority}
        onUpdate={onUpdateFloorPriority}
        onDelete={onDeleteFloorPriority}
      />
    </div>
  );
}
